/**
 * Pure aggregation helpers for CompareTab client-side re-aggregation.
 *
 * For edge_candidate mode: given a full set of edge pair rows, re-aggregate
 * per-candidate metrics over a filtered agent subset.
 *
 * For diversity/optimization: candidate sub-picker + pick_role filtering stubs
 * (full wiring deferred — see followup comment in CompareTab.tsx).
 */

import type { Evaluation, EvaluationEdgePairDetailDto } from "@/api/hooks/evaluation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Candidate = Evaluation["results"]["candidates"][number];

export interface CompareAggregate {
  destination_ip: string;
  coverage_count: number;
  total_picked: number;
  mean_ms_under_t: number | null;
  /** Route mix fractions (0–1), recomputed over filtered rows. */
  direct_share: number | null;
  onehop_share: number | null;
  twohop_share: number | null;
  /**
   * Number of picked destination agents (B) where this candidate has the
   * lowest qualifying `best_route_ms` among all qualifying candidates for
   * that B. A B with only one qualifying candidate counts as a win for that
   * sole qualifier (the operator still gets coverage from it).
   */
  wins: number;
  /**
   * Average gap between this candidate's RTT and the runner-up's RTT across
   * the B's it wins. Negative means this candidate leads (lower RTT).
   * `null` when the candidate has zero wins, or every win was uncontested
   * (only one qualifier for that B). Reported in milliseconds.
   */
  avg_delta_to_runner_up_ms: number | null;
}

// ---------------------------------------------------------------------------
// Edge-candidate re-aggregation
// ---------------------------------------------------------------------------

/**
 * Re-aggregate edge-pair rows over a picked subset of destination agents.
 *
 * Returns one `CompareAggregate` per candidate IP found in `rows`, filtered
 * to `pickedAgentIds`. When `pickedAgentIds` is empty, returns an empty array
 * so the UI can prompt the user to pick at least one agent.
 */
export function aggregateEdgeCandidates(
  rows: EvaluationEdgePairDetailDto[],
  pickedAgentIds: ReadonlySet<string>,
): CompareAggregate[] {
  if (pickedAgentIds.size === 0) return [];

  // First pass: per-B winner determination. For each picked B, find the
  // candidate with the lowest qualifying `best_route_ms` and record the gap
  // to the runner-up. Non-qualifying / unreachable rows are excluded — they
  // aren't competing on the operator's terms.
  const winnerByAgent = new Map<string, { winnerIp: string; deltaMs: number | null }>();
  const qualifiersByAgent = new Map<string, { ip: string; ms: number }[]>();
  for (const row of rows) {
    if (!pickedAgentIds.has(row.destination_agent_id)) continue;
    if (row.is_unreachable || !row.qualifies_under_t) continue;
    if (row.best_route_ms == null) continue;
    let bucket = qualifiersByAgent.get(row.destination_agent_id);
    if (!bucket) {
      bucket = [];
      qualifiersByAgent.set(row.destination_agent_id, bucket);
    }
    bucket.push({ ip: row.candidate_ip, ms: row.best_route_ms });
  }
  for (const [agentId, qualifiers] of qualifiersByAgent) {
    qualifiers.sort((a, b) => a.ms - b.ms);
    const winner = qualifiers[0];
    if (!winner) continue;
    const runnerUp = qualifiers[1];
    winnerByAgent.set(agentId, {
      winnerIp: winner.ip,
      deltaMs: runnerUp ? winner.ms - runnerUp.ms : null,
    });
  }

  // Second pass: group by candidate_ip over the picked-agent rows.
  const byCandidate = new Map<
    string,
    {
      qualifyingMs: number[];
      direct: number;
      oneHop: number;
      twoHop: number;
      totalPicked: number;
      wins: number;
      contestedDeltas: number[];
    }
  >();

  for (const row of rows) {
    if (!pickedAgentIds.has(row.destination_agent_id)) continue;

    let bucket = byCandidate.get(row.candidate_ip);
    if (!bucket) {
      bucket = {
        qualifyingMs: [],
        direct: 0,
        oneHop: 0,
        twoHop: 0,
        totalPicked: 0,
        wins: 0,
        contestedDeltas: [],
      };
      byCandidate.set(row.candidate_ip, bucket);
    }

    bucket.totalPicked++;

    // Tally win attribution from the first pass. Each (B, candidate) row
    // contributes at most one win; a contested win also contributes its
    // delta to the runner-up so we can report a mean lead per candidate.
    const winnerInfo = winnerByAgent.get(row.destination_agent_id);
    if (winnerInfo && winnerInfo.winnerIp === row.candidate_ip) {
      bucket.wins++;
      if (winnerInfo.deltaMs != null) {
        bucket.contestedDeltas.push(winnerInfo.deltaMs);
      }
    }

    // Coverage and route-mix counters BOTH gate on `qualifies_under_t`
    // (and reachability) so the Compare tab matches the backend's
    // qualifying-only aggregation. Counting non-qualifying-but-reachable
    // rows in the route-mix denominator inflated the share of long-RTT
    // routes that never satisfied T — operators saw 50/50 mixes for
    // picks where every qualifying route was direct.
    //
    // `best_route_ms` is `null` for unreachable rows; the
    // `is_unreachable` / `qualifies_under_t` guard covers that case but
    // narrow the type explicitly so we never push `null` into
    // `qualifyingMs`.
    if (!row.is_unreachable && row.qualifies_under_t) {
      if (row.best_route_ms != null) {
        bucket.qualifyingMs.push(row.best_route_ms);
      }
      switch (row.best_route_kind) {
        case "direct":
          bucket.direct++;
          break;
        case "one_hop":
          bucket.oneHop++;
          break;
        case "two_hop":
          bucket.twoHop++;
          break;
      }
    }
  }

  const aggregates: CompareAggregate[] = [];

  for (const [ip, bucket] of byCandidate) {
    const coverageCount = bucket.qualifyingMs.length;
    const meanMsUnderT =
      coverageCount > 0 ? bucket.qualifyingMs.reduce((a, b) => a + b, 0) / coverageCount : null;

    // Route-mix denominators use qualifying counts (matches the backend
    // edge-candidate aggregator in `eval/edge_candidate.rs`).
    const qualifyingTotal = bucket.direct + bucket.oneHop + bucket.twoHop;
    const direct_share = qualifyingTotal > 0 ? bucket.direct / qualifyingTotal : null;
    const onehop_share = qualifyingTotal > 0 ? bucket.oneHop / qualifyingTotal : null;
    const twohop_share = qualifyingTotal > 0 ? bucket.twoHop / qualifyingTotal : null;

    const avgDelta =
      bucket.contestedDeltas.length > 0
        ? bucket.contestedDeltas.reduce((a, b) => a + b, 0) / bucket.contestedDeltas.length
        : null;

    aggregates.push({
      destination_ip: ip,
      coverage_count: coverageCount,
      total_picked: bucket.totalPicked,
      mean_ms_under_t: meanMsUnderT,
      direct_share,
      onehop_share,
      twohop_share,
      wins: bucket.wins,
      avg_delta_to_runner_up_ms: avgDelta,
    });
  }

  return aggregates;
}

/**
 * Merge a `CompareAggregate` back onto the baseline `EvaluationCandidateDto`
 * shape so the existing `DrilldownDialog` / `EdgeCandidateTable` row rendering
 * receives a compatible object.
 *
 * Fields not recomputed here (e.g. `coverage_weighted_ping_ms`) are set to
 * `null` — the Compare table renders "—" for those.
 */
export function mergeAggregateIntoCandidate(baseline: Candidate, agg: CompareAggregate): Candidate {
  return {
    ...baseline,
    coverage_count: agg.coverage_count,
    mean_ms_under_t: agg.mean_ms_under_t,
    destinations_total: agg.total_picked,
    direct_share: agg.direct_share,
    onehop_share: agg.onehop_share,
    twohop_share: agg.twohop_share,
    // Coverage-weighted ping is intentionally deferred (formula depends on
    // full picked-pair count vs coverage; defer rather than compute wrong).
    coverage_weighted_ping_ms: null,
  };
}
